import React from 'react';
import { FiList, FiMap, FiBarChart2, FiTrendingUp, FiUsers, FiTruck, FiPackage, FiHome } from 'react-icons/fi';
import CanvasMap from './views/CanvasMap';
import CanvasGrowth from './views/CanvasGrowth';
import CanvasPms from './views/CanvasPms';
import CanvasHosts from './views/CanvasHosts';
import CanvasDrivers from './views/CanvasDrivers';
import CanvasOrders from './views/CanvasOrders';
import CanvasProperties from './views/CanvasProperties';
import { fmtNumber } from './derive';

export const VIEW_DEFS = [
  { id: 'map', label: 'Map', icon: FiMap },
  { id: 'growth', label: 'Growth', icon: FiTrendingUp },
  { id: 'pms', label: 'PMS', icon: FiBarChart2 },
  { id: 'hosts', label: 'Hosts', icon: FiUsers },
  { id: 'drivers', label: 'Drivers', icon: FiTruck },
  { id: 'orders', label: 'Orders', icon: FiPackage },
  { id: 'properties', label: 'Properties', icon: FiHome },
];

// Map a view id to (a) the row count to show in the canvas header and
// (b) whether a "View records" affordance is meaningful for that view.
function getViewMeta({
  view,
  filteredHosts,
  filteredOrders,
  drivers,
  properties,
  propertiesAvailable,
}) {
  switch (view) {
    case 'map':
      return { rowsLabel: `${fmtNumber(filteredOrders.length)} orders mapped`, recordsAvailable: false };
    case 'growth':
    case 'hosts':
    case 'pms':
      return {
        rowsLabel: `${fmtNumber(filteredHosts.length)} hosts in scope`,
        recordsAvailable: filteredHosts.length > 0,
      };
    case 'drivers':
      return {
        rowsLabel: `${fmtNumber(drivers.length)} drivers tracked`,
        recordsAvailable: drivers.length > 0,
      };
    case 'orders':
      return {
        rowsLabel: `${fmtNumber(filteredOrders.length)} orders in range`,
        recordsAvailable: filteredOrders.length > 0,
      };
    case 'properties':
      return {
        rowsLabel: propertiesAvailable
          ? `${fmtNumber(properties.length)} properties`
          : 'awaiting backend extension',
        recordsAvailable: propertiesAvailable && properties.length > 0,
      };
    default:
      return { rowsLabel: '', recordsAvailable: false };
  }
}

export default function DashboardCanvas({
  activeView,
  onChangeView,
  onOpenRecords,
  // shared data
  filters,
  range,
  filteredHosts,
  filteredOrders,
  hostOrderStats,
  drivers,
  driverWindows,
  properties,
  propertiesAvailable,
  propertiesByPms,
  // drill-down handlers
  onSelectHost,
  onSelectPms,
}) {
  const meta = getViewMeta({ view: activeView, filteredHosts, filteredOrders, drivers, properties, propertiesAvailable });

  return (
    <div className="flex h-full min-h-0 flex-col overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm">
      {/* Header: segmented control + meta + records button */}
      <div className="flex flex-wrap items-center justify-between gap-2 border-b border-zinc-100 px-3 py-2">
        <div className="flex items-center gap-1 overflow-x-auto" role="tablist" aria-label="Dashboard view">
          {VIEW_DEFS.map((v) => {
            const Icon = v.icon;
            const active = activeView === v.id;
            return (
              <button
                key={v.id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => onChangeView?.(v.id)}
                className={`group inline-flex h-8 items-center gap-1.5 whitespace-nowrap rounded-md px-2.5 text-[11px] font-medium transition-colors duration-150 motion-reduce:transition-none ${
                  active
                    ? 'bg-zinc-900 text-white shadow-sm'
                    : 'text-zinc-600 hover:bg-zinc-100 hover:text-zinc-900'
                }`}
              >
                <Icon className={`h-3.5 w-3.5 ${active ? 'text-white' : 'text-zinc-400 group-hover:text-zinc-700'}`} />
                {v.label}
              </button>
            );
          })}
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10.5px] text-zinc-500">{meta.rowsLabel}</span>
          <button
            type="button"
            onClick={() => meta.recordsAvailable && onOpenRecords?.(activeView)}
            disabled={!meta.recordsAvailable}
            title={meta.recordsAvailable ? 'Open records sheet' : 'No records to view'}
            className="inline-flex h-7 items-center gap-1 rounded-md border border-zinc-200 bg-white px-2 text-[11px] font-medium text-zinc-700 transition-colors duration-150 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50 motion-reduce:transition-none"
          >
            <FiList className="h-3 w-3" />
            View records
          </button>
        </div>
      </div>

      {/* Body — crossfade on view change via key + opacity-in animation */}
      <div className="relative flex-1 overflow-hidden p-3">
        <div
          key={activeView}
          className="h-full animate-[fadeInUp_180ms_ease-out_both] motion-reduce:animate-none"
        >
          {activeView === 'map' ? (
            <CanvasMap onPointClick={() => {}} />
          ) : activeView === 'growth' ? (
            <CanvasGrowth filteredHosts={filteredHosts} range={range} filters={filters} />
          ) : activeView === 'pms' ? (
            <CanvasPms
              filteredHosts={filteredHosts}
              propertiesByPms={propertiesByPms}
              propertiesAvailable={propertiesAvailable}
              onSelectPms={onSelectPms}
            />
          ) : activeView === 'hosts' ? (
            <CanvasHosts
              filteredHosts={filteredHosts}
              hostOrderStats={hostOrderStats}
              onSelectHost={onSelectHost}
            />
          ) : activeView === 'drivers' ? (
            <CanvasDrivers drivers={drivers} range={range} driverWindows={driverWindows} />
          ) : activeView === 'orders' ? (
            <CanvasOrders orders={filteredOrders} range={range} />
          ) : activeView === 'properties' ? (
            <CanvasProperties
              properties={properties}
              propertiesAvailable={propertiesAvailable}
              onSelectPms={onSelectPms}
            />
          ) : null}
        </div>
      </div>

      {/* Inline keyframes scoped via Tailwind arbitrary animation */}
      <style>{`
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(4px); }
          to   { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}
